import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import SafetyNotice from '../../../components/base/SafetyNotice';
import AffiliateDisclosure from '../../../components/base/AffiliateDisclosure';
import { Link } from 'react-router-dom';
import { hygieneProducts } from '../../../mocks/affiliate-products';
import SEOHead from '../../../components/base/SEOHead';

export default function HygieneNeedPage() {
  const products = hygieneProducts;

  const testimonials = [
    {
      name: 'Sabine M.',
      dog: 'Labrador, 13 Jahre',
      text: 'Die waschbaren Unterlagen haben unser Leben so viel einfacher gemacht. Keine Panik mehr, wenn Max nachts nicht einhalten kann. Und er fühlt sich nicht schuldig.',
      rating: 5,
    },
    {
      name: 'Thomas K.',
      dog: 'Schäferhund-Mix, 11 Jahre',
      text: 'Der enzymatische Reiniger ist Gold wert. Endlich riecht die Wohnung nicht mehr nach Urin, auch wenn mal was daneben geht. Bella scheint sich auch wohler zu fühlen.',
      rating: 5,
    },
    {
      name: 'Andrea L.',
      dog: 'Golden Retriever, 14 Jahre',
      text: 'Die Hygienehose war anfangs gewöhnungsbedürftig für Luna, aber jetzt trägt sie sie problemlos. Wir können wieder entspannt Besuch empfangen.',
      rating: 5,
    },
  ];

  return (
    <div className="min-h-screen bg-sand">
      <SEOHead 
        title="Hygiene & Inkontinenz beim Seniorhund | Hund im Alter"
        description="Würdevolle Lösungen für Inkontinenz beim alten Hund: Waschbare Unterlagen, Hygienehosen und praktische Tipps für den Alltag."
        keywords="Hund Inkontinenz, Seniorhund Hygiene, Inkontinenz Hund Produkte, alter Hund Blasenschwäche"
      />
      <Header />
      <main className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="text-sm text-gray-500 mb-6">
            <Link to="/" className="hover:text-teal">Start</Link>
            <span className="mx-2">/</span>
            <Link to="/beduerfnisse" className="hover:text-teal">Bedürfnisse</Link>
            <span className="mx-2">/</span>
            <span className="text-navy">Hygiene & Inkontinenz</span>
          </nav>

          {/* Page Header */}
          <div className="mb-12 bg-gradient-to-br from-teal/10 via-white to-sand rounded-3xl p-8 md:p-12 border border-teal/20">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="w-16 h-16 flex items-center justify-center bg-teal/20 rounded-2xl flex-shrink-0">
                <i className="ri-drop-fill text-5xl text-teal"></i>
              </div>
              <div className="flex-1">
                <h1 className="text-4xl md:text-5xl font-bold text-navy leading-tight mb-4">
                  Hygiene & Inkontinenz
                </h1>
                <p className="text-lg text-gray-600 leading-relaxed max-w-2xl">
                  Wenn ältere Hunde die Kontrolle über ihre Blase verlieren, ist das ein natürlicher Teil des Alterns. Hier finden Sie praktische Lösungen, die Ihrem Hund Würde bewahren und Ihren Alltag erleichtern.
                </p>
                <div className="flex flex-wrap gap-3 mt-6">
                  <span className="px-4 py-2 bg-white rounded-full text-sm text-navy font-medium shadow-sm">
                    <i className="ri-shield-check-line mr-2 text-teal"></i>Würdevoll
                  </span>
                  <span className="px-4 py-2 bg-white rounded-full text-sm text-navy font-medium shadow-sm">
                    <i className="ri-home-heart-line mr-2 text-teal"></i>Alltagstauglich
                  </span>
                  <span className="px-4 py-2 bg-white rounded-full text-sm text-navy font-medium shadow-sm">
                    <i className="ri-heart-pulse-line mr-2 text-teal"></i>Sanft
                  </span>
                </div>
              </div>
            </div>
          </div>

          <SafetyNotice />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Problem Recognition */}
              <section className="bg-white rounded-2xl p-10">
                <h2 className="text-3xl font-bold text-navy mb-6">
                  Woran Sie merken, dass dieses Bedürfnis besteht
                </h2>
                <ul className="space-y-4 text-lg text-gray-700 leading-relaxed">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-teal text-xl mt-1 flex-shrink-0"></i>
                    <span>Ihr Hund verliert nachts Urin auf seinem Liegeplatz</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-teal text-xl mt-1 flex-shrink-0"></i>
                    <span>Kleine Urinpfützen erscheinen, ohne dass der Hund es bemerkt</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-teal text-xl mt-1 flex-shrink-0"></i>
                    <span>Der Hund kann nicht mehr so lange einhalten wie früher</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-teal text-xl mt-1 flex-shrink-0"></i>
                    <span>Häufigeres Wasserlassen in kleineren Mengen</span>
                  </li>
                </ul>
              </section>

              {/* Why This Happens */}
              <section className="bg-white rounded-2xl p-10">
                <h2 className="text-3xl font-bold text-navy mb-6">
                  Warum das im Alter passiert
                </h2>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  Mit zunehmendem Alter lässt die Muskelkontrolle der Blase nach. Der Schließmuskel wird schwächer, und die Nervenverbindungen, die dem Hund signalisieren, dass die Blase voll ist, funktionieren nicht mehr so zuverlässig.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  Bei Hündinnen kann auch ein Hormonmangel nach der Kastration eine Rolle spielen. Bei Rüden können Prostataprobleme die Ursache sein.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Diese Veränderungen sind natürlich und kein Zeichen von Ungehorsam oder mangelnder Erziehung.
                </p>
              </section>

              {/* What Helps */}
              <section className="bg-white rounded-2xl p-10">
                <h2 className="text-3xl font-bold text-navy mb-6">
                  Was realistisch hilft
                </h2>
                <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                  <div>
                    <h3 className="text-xl font-semibold text-navy mb-3">Management-Strategien:</h3>
                    <ul className="space-y-3 ml-6">
                      <li className="flex items-start gap-3">
                        <span className="text-teal mt-1">•</span>
                        <span>Häufigere, kürzere Gassirunden – besonders vor dem Schlafengehen</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="text-teal mt-1">•</span>
                        <span>Wasserzugang am Abend leicht reduzieren (aber nicht komplett entziehen)</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="text-teal mt-1">•</span>
                        <span>Liegeplätze mit waschbaren Unterlagen schützen</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="text-teal mt-1">•</span>
                        <span>Enzymatische Reiniger verwenden, um Gerüche vollständig zu entfernen</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-navy mb-3">Hilfsmittel:</h3>
                    <p>Waschbare Unterlagen, Hygienehosen und spezielle Reiniger können den Alltag erheblich erleichtern und die Würde Ihres Hundes bewahren.</p>
                  </div>
                </div>
              </section>

              {/* Product Recommendations */}
              <section>
                <h2 className="text-3xl font-bold text-navy mb-6">
                  Hund-im-Alter empfiehlt
                </h2>
                
                <AffiliateDisclosure />

                <div className="space-y-8 mt-8">
                  {products.map((product, index) => (
                    <div key={index} className="bg-white rounded-2xl p-8 shadow-sm">
                      <div className="flex flex-col md:flex-row gap-6">
                        <div className="w-full md:w-48 h-48 flex-shrink-0">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-cover rounded-xl"
                          />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-navy mb-4">
                            {product.name}
                          </h3>
                          <div className="mb-4">
                            <h4 className="text-base font-semibold text-teal mb-2">
                              Warum diese Lösung hilft:
                            </h4>
                            <p className="text-base text-gray-700 leading-relaxed">
                              {product.reason}
                            </p>
                          </div>
                          <div className="mb-6">
                            <h4 className="text-base font-semibold text-navy mb-2">
                              Geeignet für:
                            </h4>
                            <ul className="space-y-2">
                              {product.suitableFor.map((item, idx) => (
                                <li key={idx} className="flex items-center gap-2 text-base text-gray-700">
                                  <i className="ri-check-line text-teal"></i>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className="flex flex-wrap gap-3">
                            <a
                              href={product.amazonLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-6 py-3 bg-navy text-white rounded-full font-medium hover:bg-navy-dark transition-colors whitespace-nowrap cursor-pointer"
                            >
                              Bei Amazon ansehen
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-32 space-y-6">
                {/* Info Box */}
                <div className="bg-sand border-l-4 border-teal rounded-xl p-6">
                  <h3 className="text-lg font-bold text-navy mb-4">
                    Wichtige Hinweise
                  </h3>
                  <ul className="space-y-3 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-information-line text-teal mt-0.5"></i>
                      <span>Inkontinenz ist keine Verhaltensstörung</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-information-line text-teal mt-0.5"></i>
                      <span>Bestrafen Sie Ihren Hund niemals dafür</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-information-line text-teal mt-0.5"></i>
                      <span>Regelmäßige Reinigung verhindert Hautprobleme</span>
                    </li>
                  </ul>
                </div>

                {/* Vet Notice */}
                <div className="bg-yellow-50 border-l-4 border-orange-400 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-navy mb-3 flex items-center gap-2">
                    <i className="ri-stethoscope-line text-orange-500"></i>
                    Wann zum Tierarzt?
                  </h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-orange-500 mt-0.5">•</span>
                      <span>Plötzlich auftretende Inkontinenz</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-500 mt-0.5">•</span>
                      <span>Blut im Urin</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-500 mt-0.5">•</span>
                      <span>Schmerzen beim Urinieren</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-500 mt-0.5">•</span>
                      <span>Vermehrter Durst</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-500 mt-0.5">•</span>
                      <span>Verhaltensänderungen</span>
                    </li>
                  </ul>
                </div>

                {/* Related Needs */}
                <div className="bg-white rounded-xl p-6">
                  <h3 className="text-lg font-bold text-navy mb-4">
                    Weitere Bedürfnisse
                  </h3>
                  <ul className="space-y-3">
                    <li>
                      <Link to="/beduerfnisse/mobilitaet-aufstehen" className="text-base text-teal hover:underline cursor-pointer">
                        Mobilität & Aufstehen →
                      </Link>
                    </li>
                    <li>
                      <Link to="/beduerfnisse/komfort-liegen" className="text-base text-teal hover:underline cursor-pointer">
                        Komfort & Liegen →
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
